import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import { HugerteEditor, TextInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { sendMailValidationSchema } from 'features/ReviseProjectProposal/validation';

const formOptions = { resolver: yupResolver(sendMailValidationSchema) };
const SendMail = forwardRef(({ handleSendMail, isLoading }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [sendMailControl, setSendMailControl] = useState(null);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isDirty, dirtyFields, isValid },
  } = useForm({ mode: 'onChange', ...formOptions });
  // #endregion Data

  // #region Events
  useImperativeHandle(
    ref,
    () => ({
      open: (_mailControl) => {
        setSendMailControl(_mailControl);
        setVisible(true);
        reset();
      },
    }),
    []
  );

  const onSubmit = () => {
    setConfirmVisible(true);
  };
  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };
  // #endregion Events

  const footerConfirmation = () => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setConfirmVisible(false)}
        autoFocus
      />
      <Button
        label={t('formLayout.action.confirm')}
        className="w-7rem"
        severity="info"
        onClick={() => {
          const data = watch();
          const content = (data?.content ?? '')?.replace(/<p>/g, '<p style="margin: 0;">');
          setConfirmVisible(false);
          handleSendMail(data?.subject, content, reset, setVisible);
        }}
      />
    </div>
  );

  return (
    <Dialog
      header={t('projectProposal.sendMail.title')}
      visible={visible}
      onHide={() => {
        setVisible(false);
        reset();
      }}
      draggable={false}
      className="w-full sm:w-8 lg:w-6"
      closable={!isLoading}
    >
      <form className="p-fluid relative ml-3" onSubmit={handleSubmit(onSubmit)}>
        <div className="relative">
          <div>
            <TextInput
              label={t('projectProposal.sendMail.subject')}
              isRequired
              name="subject"
              control={control}
              errors={errors}
              disabled={isLoading}
            />
          </div>
          <div>
            <HugerteEditor
              label={t('projectProposal.sendMail.content')}
              isRequired
              name="content"
              isFontWeightLabel
              height="200px"
              control={control}
              errors={errors}
              disabled={isLoading}
            />
          </div>
          <div className="flex flex-wrap justify-content-end mt-8">
            <Button
              label={t('formLayout.action.cancel')}
              severity="danger"
              type="button"
              onClick={() => {
                setVisible(false);
                reset();
              }}
              className="mr-2 w-8rem"
              disabled={isLoading}
            />
            <Button
              label={t('projectProposal.sendMail.send')}
              severity="info"
              type="submit"
              onClick={checkValidation}
              disabled={!isDirty || !(Object.keys(dirtyFields).length > 0) || isLoading}
              className="w-8rem"
            />
          </div>
        </div>
      </form>

      {isLoading && <Loading />}

      <Dialog
        header={<p className="text-blue-500">{t('projectProposal.sendMail.confirm')}</p>}
        visible={confirmVisible}
        position="center"
        style={{ width: '400px' }}
        onHide={() => setConfirmVisible(false)}
        draggable={false}
        footer={footerConfirmation}
      >
        <p className="text-lg">{t('projectProposal.sendMail.infoMessage')}</p>
      </Dialog>
    </Dialog>
  );
});

SendMail.propTypes = {
  handleSendMail: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

SendMail.defaultProps = {
  isLoading: false,
};

export default SendMail;
